import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, Spinner, TimeFromNow } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';
import { ControlledCodeInput, validation } from '@myhealth-raccoon/forms';

import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { authService, CodeRequestType } from 'src/services/auth';
import { verificationCodeService } from 'src/services/verificationCode';
import { useDispatch } from 'src/store';
import { bookingFlowSlice } from 'src/store/bookingFlow';
import { BackButton } from "src/components/BackButton";
import { StickyBottomBar } from 'src/components/StickyBottomBar';
import { ContentContainer } from 'src/components/ContentContainer';

const DELAY = 60000;

type Form = {
    code: string;
}

type Props = {
    codeRequestType: CodeRequestType
}

export function VerificationCode({ codeRequestType }: Props) {
    useBookingFlowStep(2);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [resendTime, setResendTime] = useState<number>();
    const [resendIsDisabled, setResendIsDisabled] = useState<boolean>();
    const [didSubmit, setDidSubmit] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { handleSubmit, control, formState: { isSubmitting, errors }, reset, watch, setError } = useForm<Form>();

    const doSubmit = useCallback(async ({ code }: Form) => {
        setDidSubmit(true);
        try {
            await authService.VerifyCode(code, codeRequestType);
            dispatch(bookingFlowSlice.actions.setCodeRequestType(codeRequestType));
            navigate('/app/reserve-booking', { replace: true });
        } catch (e) {
            if (e instanceof Error) {
                setErrorMessage(e.message || e.name);
                setError("code", { message: e.message || e.name })
            }
            reset({ code: '' });
            setDidSubmit(false);
        }
    }, [codeRequestType, dispatch, navigate, reset, setError]);

    const { invoke: generateCode, isLoading: generatingCode } = useAsyncData({
        service: verificationCodeService,
        method: verificationCodeService.GenerateCode,
        invokeOnMount: false,
    }, codeRequestType);

    const resendCode = useCallback(async () => {
        try {
            await generateCode();
            toast("We've sent a new code!");
            setResendIsDisabled(true);
            setErrorMessage('');
        } catch {
            /* */
        }
    }, [generateCode]);

    useEffect(() => {
        if (resendIsDisabled) {
            const timeout = setTimeout(() => {
                setResendIsDisabled(false);
                setResendTime(undefined);
            }, DELAY);
            setResendTime(Date.now() + DELAY);

            return () => clearTimeout(timeout);
        }
    }, [resendIsDisabled]);

    useEffect(() => {
        if (!didSubmit && watch('code')) {
            handleSubmit(doSubmit)();
        }
        const { unsubscribe } = watch(() => {
            if (!didSubmit) {
                handleSubmit(doSubmit)();
            }
        });
        return unsubscribe;
    }, [handleSubmit, doSubmit, watch, didSubmit]);

    return <>
        <ContentContainer>
            <h1 className='text-2xl text-gray-900 font-semibold'>Enter your code</h1>

            <p className='my-2.5 text-base'>
                We sent a verification code to your {codeRequestType === "Email" ? "email" : "number"}.
                <br />
                The code will expire after 5 minutes.
            </p>
        </ContentContainer>
        <form onSubmit={handleSubmit(doSubmit)} className='flex flex-col lg:flex-none flex-1'>
            <ContentContainer className='flex flex-col gap-y-6 mt-6 sm:mt-8 flex-1'>
                <ControlledCodeInput
                    control={control}
                    name='code'
                    maxLength={6}
                    placeholder='000000'
                    className='sm:w-fit text-4xl sm:text-5xl'
                    rules={{
                        validate: validation.composeValidators(
                            validation.required(" "),
                            validation.minLength(6, " "))
                    }}
                    fieldError={!watch("code") && errorMessage ? errors.code : undefined}
                />
                {errorMessage &&
                    <div className='text-error-500'>
                        {errorMessage}
                    </div>
                }
                <div className='text-sm text-center'>
                    Didn't receive the code?{' '}
                    <button type='button' disabled={generatingCode || resendIsDisabled} className='font-semibold text-primary disabled:text-gray-300' onClick={resendCode}>
                        Click to resend
                    </button>
                    {' '}
                    {generatingCode && <Spinner size='xs' color='current' className='inline-block align-middle' />}
                    {'  '}
                    {resendIsDisabled && resendTime &&
                        <TimeFromNow explicit time={resendTime} />
                    }
                </div>
            </ContentContainer>
            <StickyBottomBar>
                <BackButton
                    onClick={() => navigate(-1)}
                    label="Back" />
                <Button className='flex-1 text-center' disabled={isSubmitting} type='submit'>Verify</Button>
            </StickyBottomBar>
        </form>
    </>
}